import React, { createContext, useContext, useMemo, useState } from 'react';
import { Tileset } from '../types/tileset';

interface TilesetState {
  hoveredColor: Nullable<string>;
  tileset: Nullable<Tileset>;
  tilesetMasked: boolean;
  tilesetShown: boolean;
  setHoveredColor: (c: Nullable<string>) => void;
  setTileset: (t: Nullable<Tileset>) => void;
  setTilesetMasked: (m: boolean) => void;
  setTilesetShown: (s: boolean) => void;
}

const initialState = {
  hoveredColor: null,
  tileset: undefined,
  tilesetMasked: false,
  tilesetShown: true,
  setHoveredColor: () => {},
  setTileset: () => {},
  setTilesetMasked: () => {},
  setTilesetShown: () => {},
};

export const TilesetContext = createContext<TilesetState>(initialState);

export const TilesetContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [tileset, setTileset] = useState<Nullable<Tileset>>(
    initialState.tileset,
  );
  const [tilesetMasked, setTilesetMasked] = useState(
    initialState.tilesetMasked,
  );
  const [tilesetShown, setTilesetShown] = useState(initialState.tilesetShown);

  // A string representing the color of the tileset that is being hovered over (or null).
  const [hoveredColor, setHoveredColor] = useState<Nullable<string>>(
    initialState.hoveredColor,
  );

  const value = useMemo(
    () => ({
      hoveredColor,
      tileset,
      tilesetMasked,
      tilesetShown,
      setHoveredColor,
      setTileset,
      setTilesetMasked,
      setTilesetShown,
    }),
    [
      hoveredColor,
      tileset,
      tilesetMasked,
      tilesetShown,
      setHoveredColor,
      setTileset,
      setTilesetMasked,
      setTilesetShown,
    ],
  );

  return (
    <TilesetContext.Provider value={value}>{children}</TilesetContext.Provider>
  );
};

export const useTilesetContext = () => useContext(TilesetContext);
